/**
 * One input per field type. The form never switches on a column name — it
 * switches on `field.type`, which is what keeps a new config type from needing
 * a frontend release.
 */

import { useId, useState, type KeyboardEvent } from 'react'
import type { ConfigRow, FieldSchema, StatusNoteField } from '../api/types'
import { hhmmToMinutes, minutesToHhmm } from '../lib/format'
import { joinStatusNote, splitStatusNote } from '../lib/statusNote'

export interface FieldInputProps {
  field: FieldSchema
  value: unknown
  error?: string
  required?: boolean
  disabled?: boolean
  /** Rows of the lookup source, already fetched by the form. */
  lookupRows?: ConfigRow[]
  onChange: (name: string, value: unknown) => void
  /** Fired with the whole source row so the form can run `autofill`. */
  onLookupPicked?: (field: FieldSchema, sourceRow: ConfigRow | null) => void
}

/**
 * A text input with a suggestion list drawn directly beneath it.
 *
 * Not a `<datalist>`: the browser draws that popup itself, outside the page,
 * and inside the sliding drawer it lands detached from the box it belongs to.
 * This list is part of the page, so it sits under its input wherever the input
 * is. Free text is still accepted — suggestions are a nudge, not a closed set
 * (see TextField.suggestions).
 */
function SuggestInput({
  id,
  listId,
  className,
  value,
  maxLength,
  suggestions,
  disabled,
  invalid,
  onChange,
}: {
  id: string
  listId?: string
  className: string
  value: string
  maxLength?: number
  suggestions: string[]
  disabled?: boolean
  invalid: boolean
  onChange: (value: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)

  // Empty box: every suggestion. Otherwise the ones starting with what has
  // been typed, so "in" offers "In process" and a full sentence offers nothing.
  const needle = value.trim().toLowerCase()
  const matches = suggestions.filter(
    (s) => !needle || (s.toLowerCase().startsWith(needle) && s.toLowerCase() !== needle),
  )
  const showList = open && matches.length > 0

  const pick = (suggestion: string) => {
    onChange(suggestion)
    setOpen(false)
    setActive(-1)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!matches.length) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setActive((i) => (i + 1) % matches.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setOpen(true)
      setActive((i) => (i <= 0 ? matches.length - 1 : i - 1))
    } else if (event.key === 'Enter' && showList && active >= 0) {
      event.preventDefault()
      pick(matches[active])
    } else if (event.key === 'Escape' && showList) {
      event.preventDefault()
      setOpen(false)
    }
  }

  return (
    <div className="suggest">
      <input
        id={id}
        className={className}
        value={value}
        maxLength={maxLength}
        disabled={disabled}
        autoComplete="off"
        role={listId ? 'combobox' : undefined}
        aria-expanded={listId ? showList : undefined}
        aria-controls={listId}
        aria-autocomplete={listId ? 'list' : undefined}
        aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
        aria-invalid={invalid}
        onChange={(event) => {
          onChange(event.target.value)
          setOpen(true)
          setActive(-1)
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      {listId && showList ? (
        <ul id={listId} className="suggest__list" role="listbox">
          {matches.map((suggestion, index) => (
            <li
              key={suggestion}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className={`suggest__item ${index === active ? 'is-active' : ''}`}
              // mousedown, not click: click fires after the input's blur has
              // already closed the list.
              onMouseDown={(event) => {
                event.preventDefault()
                pick(suggestion)
              }}
              onMouseEnter={() => setActive(index)}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/**
 * HH:MM in, minutes out.
 *
 * Holds its own text while the user types — deriving the input's value from the
 * parsed number would fight anyone typing "0", "08", "08:0". Only a complete,
 * valid value is pushed up; anything else reports as invalid rather than
 * silently resolving to something the user did not type, which is exactly the
 * failure mode the env-var parser had.
 */
function MinuteOfDayInput({ field, value, error, required, disabled, onChange }: FieldInputProps) {
  const id = useId()
  const [text, setText] = useState(() => minutesToHhmm(value as number | null))
  const [localError, setLocalError] = useState<string | null>(null)

  // Resync when the value changes from outside — an autofill from the zone
  // lookup is the case that matters. Adjusted during render rather than in an
  // effect: React re-runs this component before touching the DOM, so the input
  // never paints the stale text, and there is no second commit.
  const [syncedValue, setSyncedValue] = useState(value)
  if (syncedValue !== value) {
    setSyncedValue(value)
    setText(minutesToHhmm(value as number | null))
    setLocalError(null)
  }

  const commit = (raw: string) => {
    const parsed = hhmmToMinutes(raw)
    if (parsed === undefined) {
      setLocalError('Use HH:MM, e.g. 08:00.')
      return
    }
    setLocalError(null)
    onChange(field.name, parsed)
  }

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {field.label}
        {required ? <span className="field__required">*</span> : null}
      </label>
      <input
        id={id}
        className={`input input--time ${error || localError ? 'input--error' : ''}`}
        value={text}
        placeholder="HH:MM"
        inputMode="numeric"
        disabled={disabled}
        onChange={(event) => setText(event.target.value)}
        onBlur={(event) => commit(event.target.value)}
        aria-invalid={Boolean(error || localError)}
      />
      {field.help ? <p className="field__help">{field.help}</p> : null}
      {localError || error ? <p className="field__error">{localError ?? error}</p> : null}
    </div>
  )
}

/**
 * A status from a closed list plus an optional comment, stored as one value
 * (see lib/statusNote). The dropdown is a real select, so nobody can invent a
 * status; the comment is free text with a visible limit.
 */
function StatusNoteInput({
  field,
  value,
  error,
  required,
  disabled,
  onChange,
}: FieldInputProps & { field: StatusNoteField }) {
  const id = useId()
  const { status, note } = splitStatusNote(field, value)
  const set = (next: { status?: string; note?: string }) =>
    onChange(field.name, joinStatusNote(field, { status, note, ...next }))
  const over = note.trim().length > field.note_max_len

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {field.label}
        {required ? <span className="field__required">*</span> : null}
      </label>
      <div className="status-note">
        <select
          id={id}
          className={`select ${error && !status ? 'select--error' : ''}`}
          value={status}
          disabled={disabled}
          onChange={(event) => set({ status: event.target.value })}
        >
          <option value="">{field.nullable ? '— none —' : 'Choose…'}</option>
          {field.statuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <textarea
          className={`textarea ${over ? 'input--error' : ''}`}
          value={note}
          rows={2}
          placeholder="Comment (optional)"
          disabled={disabled}
          aria-label={`${field.label} comment`}
          onChange={(event) => set({ note: event.target.value })}
        />
        <span className={`status-note__count ${over ? 'status-note__count--over' : ''}`}>
          {note.trim().length} / {field.note_max_len}
        </span>
      </div>
      {field.help ? <p className="field__help">{field.help}</p> : null}
      {error ? <p className="field__error">{error}</p> : null}
    </div>
  )
}

export function FieldInput(props: FieldInputProps) {
  const { field, value, error, required, disabled, lookupRows, onChange, onLookupPicked } = props
  const id = useId()

  if (field.type === 'minute_of_day') return <MinuteOfDayInput {...props} />
  if (field.type === 'status_note') return <StatusNoteInput {...props} field={field} />

  if (field.type === 'bool') {
    return (
      <div className="field">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={Boolean(value)}
            disabled={disabled}
            onChange={(event) => onChange(field.name, event.target.checked)}
          />
          <span>
            <span className="checkbox__text">{field.label}</span>
            {field.help ? <p className="field__help">{field.help}</p> : null}
          </span>
        </label>
        {error ? <p className="field__error">{error}</p> : null}
      </div>
    )
  }

  if (field.type === 'enum') {
    return (
      <div className="field">
        <label className="field__label" htmlFor={id}>
          {field.label}
          {required ? <span className="field__required">*</span> : null}
        </label>
        <select
          id={id}
          className={`select ${error ? 'select--error' : ''}`}
          value={String(value ?? '')}
          disabled={disabled}
          onChange={(event) => onChange(field.name, event.target.value)}
        >
          {field.nullable ? <option value="">— none —</option> : <option value="" disabled>Choose…</option>}
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {field.help ? <p className="field__help">{field.help}</p> : null}
        {error ? <p className="field__error">{error}</p> : null}
      </div>
    )
  }

  if (field.type === 'lookup') {
    const rows = lookupRows ?? []
    return (
      <div className="field">
        <label className="field__label" htmlFor={id}>
          {field.label}
          {required ? <span className="field__required">*</span> : null}
        </label>
        <select
          id={id}
          className={`select ${error ? 'select--error' : ''}`}
          value={String(value ?? '')}
          disabled={disabled || rows.length === 0}
          onChange={(event) => {
            const picked = event.target.value
            onChange(field.name, picked === '' ? null : picked)
            const sourceRow =
              rows.find((row) => String(row[field.value_field]) === picked) ?? null
            onLookupPicked?.(field, sourceRow)
          }}
        >
          <option value="">— none —</option>
          {rows.map((row) => (
            <option key={String(row[field.value_field])} value={String(row[field.value_field])}>
              {String(row[field.label_field])}
            </option>
          ))}
        </select>
        {field.help ? <p className="field__help">{field.help}</p> : null}
        {error ? <p className="field__error">{error}</p> : null}
      </div>
    )
  }

  if (field.type === 'int') {
    return (
      <div className="field">
        <label className="field__label" htmlFor={id}>
          {field.label}
          {required ? <span className="field__required">*</span> : null}
        </label>
        <input
          id={id}
          type="number"
          className={`input ${error ? 'input--error' : ''}`}
          value={value === null || value === undefined ? '' : String(value)}
          min={field.min}
          max={field.max}
          disabled={disabled}
          onChange={(event) =>
            onChange(field.name, event.target.value === '' ? null : Number(event.target.value))
          }
        />
        {field.help ? <p className="field__help">{field.help}</p> : null}
        {error ? <p className="field__error">{error}</p> : null}
      </div>
    )
  }

  // text and timestamp. A timestamp is never editable, so it renders as a
  // disabled input rather than getting its own branch.
  const isText = field.type === 'text'
  const listId = isText && field.suggestions?.length ? `${id}-list` : undefined

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {field.label}
        {required ? <span className="field__required">*</span> : null}
      </label>
      {isText && field.multiline ? (
        <textarea
          id={id}
          className={`textarea ${error ? 'input--error' : ''}`}
          value={String(value ?? '')}
          maxLength={field.max_len}
          disabled={disabled}
          onChange={(event) => onChange(field.name, event.target.value)}
        />
      ) : (
        <SuggestInput
          id={id}
          listId={listId}
          className={`input ${error ? 'input--error' : ''}`}
          value={String(value ?? '')}
          maxLength={isText ? field.max_len : undefined}
          suggestions={isText ? (field.suggestions ?? []) : []}
          disabled={disabled}
          invalid={Boolean(error)}
          onChange={(next) => onChange(field.name, next)}
        />
      )}
      {field.help ? <p className="field__help">{field.help}</p> : null}
      {error ? <p className="field__error">{error}</p> : null}
    </div>
  )
}
